import {useFloatingTree, useListItem} from '@floating-ui/react';
import {
  getColorFromSeverity,
  Icon,
  Severity,
  Show,
  Spinner,
  Text,
  TextProps,
  ThemeIconKey,
} from 'platform/foundation';
import {cva} from 'platform/shared';
import {match} from 'ts-pattern';

import {MouseEventHandler, ReactElement, ReactNode, useContext} from 'react';

import {isNotNil} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {MenuContext} from './MenuContext';

export interface DropdownItemProps extends TestIdProps {
  label: string | ReactElement<TextProps, typeof Text>;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  isDisabled?: boolean;
  prefix?: ReactNode;
  suffix?: ReactElement;
  leftIcon?: ThemeIconKey;
  isLoading?: boolean;
  severity?: Severity;
  isActive?: boolean;
  closeOnClick?: boolean;
  onMouseEnter?: () => void;
}

const menuItemVariants = cva(
  'relative flex max-h-9 w-full flex-auto select-none items-center rounded-[var(--radius-medium)] p-2 text-start !text-[length:var(--text-size-small)] font-[var(--text-weight-default)] leading-[var(--text-leading-small)] tracking-[0] no-underline [outline:0]',
  {
    variants: {
      state: {
        default:
          'pointer-events-auto bg-transparent text-[var(--color-primary)] hover:bg-[var(--color-palettes-blue-10-100)] hover:text-[var(--color-palettes-blue-80-100)] active:bg-[var(--color-palettes-blue-10-100)] active:text-[var(--color-palettes-blue-80-100)] focus:bg-[var(--color-palettes-blue-10-100)] focus:text-[var(--color-palettes-blue-80-100)]',
        active:
          'pointer-events-auto bg-[var(--color-palettes-blue-10-100)] text-[var(--color-palettes-blue-80-100)] hover:bg-[var(--color-palettes-blue-10-100)] hover:text-[var(--color-palettes-blue-80-100)] active:bg-[var(--color-palettes-blue-10-100)] active:text-[var(--color-palettes-blue-80-100)] focus:bg-[var(--color-palettes-blue-10-100)] focus:text-[var(--color-palettes-blue-80-100)]',
        disabled:
          'pointer-events-none bg-[var(--color-white)] text-[var(--color-palettes-neutral-900-40)] hover:bg-[var(--color-white)] hover:text-[var(--color-palettes-neutral-900-40)] active:bg-[var(--color-white)] active:text-[var(--color-palettes-neutral-900-40)] focus:bg-[var(--color-palettes-blue-10-100)] focus:text-[var(--color-palettes-neutral-900-40)]',
      },
    },
    defaultVariants: {
      state: 'default',
    },
  }
);

function getMenuItemState(isDisabled: boolean | undefined, isActive: boolean | undefined) {
  return match({isDisabled: Boolean(isDisabled), isActive: Boolean(isActive)})
    .with({isDisabled: true}, () => 'disabled' as const)
    .with({isActive: true}, () => 'active' as const)
    .otherwise(() => 'default' as const);
}

export function DropdownItem(props: DropdownItemProps) {
  const menu = useContext(MenuContext);
  const item = useListItem({label: typeof props.label === 'string' ? props.label : undefined});
  const tree = useFloatingTree();
  const isActive = item.index === menu.activeIndex || props.isActive;
  const iconColor = props.severity ? getColorFromSeverity(props.severity) : undefined;

  const icon = isNotNil(props.leftIcon) ? (
    <Icon color={iconColor} value={props.leftIcon} size={4} />
  ) : undefined;

  const content = (
    <>
      {props.label}
      <Show when={props.isLoading}>
        <Spinner variant="overlay" size="small" />
      </Show>
    </>
  );

  const shouldWrap = icon || props.suffix;

  const children = shouldWrap ? (
    <span className="pointer-events-none flex-1">{content}</span>
  ) : (
    content
  );

  const leadingContent = match({icon, prefix: props.prefix})
    .when(
      ({icon: leadingIcon}) => isNotNil(leadingIcon),
      ({icon: leadingIcon}) => leadingIcon
    )
    .when(
      ({prefix}) => isNotNil(prefix),
      ({prefix}) => <>{prefix}</>
    )
    .otherwise(() => undefined);

  return (
    <button
      ref={item.ref}
      tabIndex={isActive ? 0 : -1}
      className={menuItemVariants({state: getMenuItemState(props.isDisabled, isActive)})}
      disabled={props.isDisabled}
      type="button"
      role="menuitem"
      onMouseEnter={props.onMouseEnter}
      {...menu.getItemProps({
        onClick(event: React.MouseEvent<HTMLButtonElement>) {
          props.onClick?.(event);
          if (props.closeOnClick !== false) {
            tree?.events.emit('click');
            menu.setOnItemClick();
          }
        },
      })}
      data-testid={suffixTestId('dropdownItem', props)}
    >
      <span className="me-3 shrink-0 text-[0.8rem] [&_svg]:!h-4 [&_svg]:!w-4 [&_svg[data-iconkey^='flags/']]:!h-5 [&_svg[data-iconkey^='flags/']]:!w-7">
        {leadingContent}
      </span>
      {children}
      <Show when={props.suffix}>
        <span className="ms-3 text-[var(--color-palettes-blue-80-100)] opacity-100 [&_svg]:!h-4 [&_svg]:!w-4 [&_svg[data-iconkey^='flags/']]:!h-5 [&_svg[data-iconkey^='flags/']]:!w-7">
          {props.suffix}
        </span>
      </Show>
    </button>
  );
}
